import { useMemo } from "react";

import { orderBy } from "lodash";

import { PortfolioFamilyPath } from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanFamilyPath";

import {
  FAM,
  FARTHER_MODELS_TYPE_NAME,
} from "@src/multiCustodian/components/Advisor/Investments/constants";

// Define expected path levels
const PATH_LEVEL = {
  PROVIDER: 2,
  SERIES: 1,
  ASSET_ALLOCATION: 0,
} as const;

// Number of expected path levels
const EXPECTED_PATH_LEVELS = 3;

type FamilyPath =
  | {
      provider: string;
      series: string;
      assetAllocation: string;
    }
  | undefined;

/**
 * The family path represents the path of the portfolio plan.
 * It is a list containing Implementation Families structured in three levels:
 * provider, series, and asset allocation.
 */

export default function useCreateFamilyPathFromData(
  portfolioFamilyPath?: PortfolioFamilyPath[]
): FamilyPath {
  return useMemo(() => {
    // Early return if data is missing
    if (
      !portfolioFamilyPath ||
      portfolioFamilyPath.length !== EXPECTED_PATH_LEVELS
    ) {
      return undefined;
    }

    // Check if we have the expected number of unique path lengths
    const uniquePathLengths = new Set(
      portfolioFamilyPath.map((f) => f.pathLength)
    );
    if (uniquePathLengths.size !== EXPECTED_PATH_LEVELS) {
      return undefined;
    }

    // Sort paths by path length, where the provider is the farthest asset management
    const sortedPaths = orderBy(
      portfolioFamilyPath,
      (p) => p.pathLength,
      "asc"
    );

    return {
      // for FAM provider we should have always `Farther Asset Management`
      provider:
        sortedPaths[PATH_LEVEL.PROVIDER].name === FAM
          ? FARTHER_MODELS_TYPE_NAME
          : sortedPaths[PATH_LEVEL.PROVIDER].name,
      series: sortedPaths[PATH_LEVEL.SERIES].name,
      assetAllocation: sortedPaths[PATH_LEVEL.ASSET_ALLOCATION].name,
    };
  }, [portfolioFamilyPath]);
}
